/**
 * Final Draft tags kept on FDX import (RVD-67). Every read of the backend tag-count fields lives
 * here, so a rename touches one file.
 *
 * Contract: `POST /projects/parse`, `POST /projects` (projects and versions) and
 * `GET /projects/import-jobs/:id` all return `data.source_tag_count`, `data.imported_tag_count`
 * and `data.skipped_tag_count`. Tags that overlap or cannot be read are skipped; the import
 * itself never fails because of tags. Counts at the top level are read too, for older builds.
 */

/** What an import kept and skipped. */
export type FinalDraftTagCounts = {
  imported: number;
  skipped: number;
};

/** What a parsed file holds before import. */
export type ParsedFinalDraftTags = {
  /** Tags in the file. */
  source: number;
  /** Tags RivetAI will keep (the rest overlap or cannot be read). */
  kept: number;
};

type TagCountFields = {
  source_tag_count?: unknown;
  imported_tag_count?: unknown;
  skipped_tag_count?: unknown;
};

function count(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) && value >= 0
    ? Math.floor(value)
    : undefined;
}

function asFields(value: unknown): TagCountFields | undefined {
  return value && typeof value === "object" ? (value as TagCountFields) : undefined;
}

/** Reads a field from `{ data: { field } }`, `{ field }`, or an already unwrapped `data` object. */
function readCount(response: unknown, field: keyof TagCountFields): number | undefined {
  const outer = asFields(response);
  const inner = asFields((response as { data?: unknown } | null | undefined)?.data);
  return count(inner?.[field]) ?? count(outer?.[field]);
}

/** Final Draft tags in a parsed file; zeros for other formats or older backends. */
export function finalDraftTagsFromParse(parsed: unknown): ParsedFinalDraftTags {
  const source = readCount(parsed, "source_tag_count") ?? 0;
  const imported = readCount(parsed, "imported_tag_count");
  const skipped = readCount(parsed, "skipped_tag_count");
  const kept = imported ?? (skipped === undefined ? source : source - skipped);
  return { source, kept: Math.max(0, Math.min(source, kept)) };
}

/** Counts reported by an import, or null when the response says nothing about tags. */
export function finalDraftTagCountsFromImport(response: unknown): FinalDraftTagCounts | null {
  const imported = readCount(response, "imported_tag_count");
  const skipped = readCount(response, "skipped_tag_count");
  if (imported === undefined && skipped === undefined) return null;
  return { imported: imported ?? 0, skipped: skipped ?? 0 };
}

function tags(n: number): string {
  return n === 1 ? "1 Final Draft tag" : `${n} Final Draft tags`;
}

/** Import dialog line, shown when the file has tags. */
export function finalDraftTagsFoundMessage({ source, kept }: ParsedFinalDraftTags): string {
  const found = `This file has ${tags(source)}.`;
  if (kept >= source) return `${found} RivetAI keeps them.`;
  if (kept === 0) return `${found} They overlap or can't be read, so RivetAI skips them.`;
  return `${found} RivetAI keeps ${kept} of them.`;
}

export const FINAL_DRAFT_BREAKDOWN_OFF_HELPER =
  "Turn on to let AI add more tags. Your Final Draft tags stay.";
export const FINAL_DRAFT_BREAKDOWN_ON_HELPER = "AI adds more tags. Your Final Draft tags stay.";
/** Features turned off only because Script breakdown starts off for a tagged file. */
export type HeldOffFeatures = { budget: boolean; schedule: boolean };

/** Upload new version note naming only the features that start off; null when none do. */
export function finalDraftHeldOffFeaturesNote({
  budget,
  schedule,
}: HeldOffFeatures): string | null {
  if (budget && schedule)
    return "Budget and Schedule start off because they need Script breakdown.";
  if (budget) return "Budget starts off because it needs Script breakdown.";
  if (schedule) return "Schedule starts off because it needs Script breakdown.";
  return null;
}
/** Unlicensed projects, where the free scene breakdown always runs on import. */
export const FINAL_DRAFT_FREE_BREAKDOWN_HELPER =
  "The free scene breakdown adds more tags. Your Final Draft tags stay.";

/** Completion line after import; null when the file had no Final Draft tags. */
export function finalDraftTagsKeptMessage(counts: FinalDraftTagCounts | null): string | null {
  if (!counts || (counts.imported === 0 && counts.skipped === 0)) return null;
  if (counts.imported === 0) {
    return `Skipped ${tags(counts.skipped)} that overlapped or could not be read.`;
  }
  const kept = `Kept ${counts.imported === 1 ? "1 tag" : `${counts.imported} tags`} from Final Draft.`;
  if (counts.skipped === 0) return kept;
  return `${kept} Skipped ${counts.skipped} that overlapped or could not be read.`;
}
